/**
 * Size limits for the Knowledge Agent's single-file attachment, plus the
 * user-facing copy for each. One module so the tooltip and every error
 * message quote the same numbers the code enforces.
 *
 * Where the numbers come from:
 *   - Text/code files are sent as-is, so their raw size is the text size.
 *   - PDF / Word / Excel are converted to text in the browser; the raw file
 *     may be up to 5 MB (mirrors the 5,000,000 `sizeBytes` cap in the shared
 *     AgentAskRequest schema, the backend AskBody and the Python agent).
 *   - Whatever text we end up sending must fit in 1 MB once JSON-encoded:
 *     the backend's JSON body limit is 1 MiB for the whole request and
 *     `contentText` is capped at 1,000,000 characters. Counting encoded
 *     bytes (not characters) keeps quote-, newline- and non-ASCII-heavy
 *     files from passing this check and then failing on the server.
 */

export const ATTACH_TEXT_MAX_BYTES = 1_000_000;
export const ATTACH_BINARY_MAX_BYTES = 5_000_000;
export const ATTACH_TEXT_PAYLOAD_MAX_BYTES = 1_000_000;

export type AttachmentKind = 'text' | 'pdf' | 'word' | 'excel';

const KIND_LABEL: Record<Exclude<AttachmentKind, 'text'>, string> = {
  pdf: 'PDF',
  word: 'Word',
  excel: 'Excel',
};

export const ATTACH_LIMITS_HINT =
  'Attach a file: text, Markdown, CSV, JSON, YAML or code (up to 1 MB), ' +
  'or PDF, Word or Excel (up to 5 MB)';

/** Size of `text` as it travels in the request body (UTF-8, JSON-escaped). */
export function encodedTextBytes(text: string): number {
  return new TextEncoder().encode(JSON.stringify(text)).length;
}

/** Error copy when the picked file is too big to even read, else null. */
export function rawSizeError(kind: AttachmentKind, bytes: number): string | null {
  if (kind === 'text') {
    return bytes > ATTACH_TEXT_MAX_BYTES ? 'Text and code files must be 1 MB or smaller.' : null;
  }
  return bytes > ATTACH_BINARY_MAX_BYTES
    ? `${KIND_LABEL[kind]} files must be 5 MB or smaller.`
    : null;
}

/** Error copy when the text we would send is over the 1 MB budget, else null. */
export function extractedTextError(kind: AttachmentKind, text: string): string | null {
  if (encodedTextBytes(text) <= ATTACH_TEXT_PAYLOAD_MAX_BYTES) return null;
  if (kind === 'text') {
    return 'This file has more than 1 MB of text once sent. Attach a shorter excerpt instead.';
  }
  return `This ${KIND_LABEL[kind]} file contains more than 1 MB of text — too much for one question. Attach a shorter excerpt instead.`;
}
